import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LegalLinks } from "./Legal";

const HERO = "https://images.pexels.com/photos/33355790/pexels-photo-33355790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-xl bg-stone-900 flex items-center justify-center">
              <span className="text-champagne font-serif text-lg">A</span></div>
            <span className="font-serif text-lg">Notre Oui</span>
          </Link>
          <h1 className="font-serif text-3xl text-stone-900">{title}</h1>
          <p className="text-stone-500 mt-2 mb-8">{subtitle}</p>
          {children}
          <LegalLinks className="text-xs text-stone-400 mt-8" />
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Domaine de mariage suisse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [code, setCode] = useState(""); const [mfaStep, setMfaStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(email, password, code);
      toast.success("Bienvenue !");
      if (u.role === "SUPER_ADMIN") nav("/super-admin");
      else if (u.vendor_id || u.role === "VENDOR_OWNER") nav("/vendor");
      else if (u.wedding_id) nav("/app");
      else nav("/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "MFA_REQUIRED") { setMfaStep(true); toast.info("Saisissez votre code d'authentification à 6 chiffres."); }
      else toast.error(apiErr(detail));
    } finally { setLoading(false); }
  };
  return (
    <Shell title="Bon retour" subtitle="Reconnectez-vous à votre espace de planification.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>E-mail</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" /></div>
        <div><Label>Mot de passe</Label>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11" /></div>
        {mfaStep && (
          <div><Label>Code d'authentification (2FA)</Label>
            <Input data-testid="login-mfa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 chiffres ou code de secours" className="mt-1.5 h-11" autoFocus />
            <p className="text-xs text-stone-400 mt-1">Ouvrez votre application d'authentification, ou utilisez un code de secours.</p></div>
        )}
        <Button data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-full">
          {loading ? "Connexion…" : (mfaStep ? "Valider le code" : "Se connecter")}</Button>
      </form>
      <p className="text-sm text-stone-500 mt-6">Pas encore de compte ?{" "}
        <Link to="/register" className="text-champagne font-medium">Créer un compte</Link></p>
    </Shell>
  );
}

export function Register({ mode = "couple" }) {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register({ ...f, account_type: mode }); toast.success("Compte créé !"); nav(mode === "vendor" ? "/vendor" : "/onboarding"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isVendor = mode === "vendor";
  return (
    <Shell title={isVendor ? "Créons votre profil prestataire" : "Créons votre mariage"}
      subtitle={isVendor ? "Un compte professionnel sécurisé. Vous configurerez votre entreprise ensuite." : "Un compte personnel et sécurisé. Vous inviterez votre partenaire ensuite."}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input data-testid="reg-firstname" value={f.first_name} onChange={set("first_name")} required className="mt-1.5 h-11" /></div>
          <div><Label>Nom</Label><Input data-testid="reg-lastname" value={f.last_name} onChange={set("last_name")} className="mt-1.5 h-11" /></div>
        </div>
        <div><Label>E-mail</Label><Input data-testid="reg-email" type="email" value={f.email} onChange={set("email")} required className="mt-1.5 h-11" /></div>
        <div><Label>Mot de passe</Label><Input data-testid="reg-password" type="password" value={f.password} onChange={set("password")} required className="mt-1.5 h-11" /></div>
        <Button data-testid="reg-submit" disabled={loading} className="w-full h-11 rounded-full">
          {loading ? "Création…" : "Créer mon compte"}</Button>
      </form>
      <p className="text-sm text-stone-500 mt-6">Déjà inscrit·e ?{" "}
        <Link to="/login" className="text-champagne font-medium">Se connecter</Link></p>
      <p className="text-sm text-stone-400 mt-2">
        {isVendor ? <Link to="/register/couple" className="hover:text-stone-700">Plutôt organiser mon mariage ?</Link>
                  : <Link to="/register/vendor" className="hover:text-stone-700">Vous êtes prestataire ?</Link>}</p>
    </Shell>
  );
}
